import { CompanyDocument, PersonalDocument } from '@scansource-brazil/zeus-core-ui/utils';
import {
  SalesCommissionFilters,
  SalesCommissionFiltersOutputDTO,
  SalesCommissionInputDTO,
  SalesCommissionInstallment,
  SalesCommissionInstallmentInputDTO,
  SalesCommissionItemsInputDTO,
  SalesCommissionListItem,
  SalesCommissionListItemInputDTO,
} from '~/models/Commission';
import {
  SalesCommissionStatusDTOEnum,
  SalesCommissionStatusEnum,
} from '~/enums/CommissionStatus.enum';
import { convertToBrCurrency } from '@scansource-brazil/zeus-core-ui/string';
import { formatToBrazilianDate } from '~/utils/DateMethods';
import { getObjectKeys } from '@scansource-brazil/zeus-core-ui/data';
import { type GeneralObject } from '@scansource-brazil/zeus-core-ui/types';

export class AdaptSalesCommission {
  public static toModel(inputDTO?: SalesCommissionInputDTO) {
    try {
      const dto = new SalesCommissionInputDTO(inputDTO);
      return new SalesCommission({
        salesCommissionId: dto.salesCommissionId,
        invoiceNumber: dto.invoiceNumber,
        invoiceSerieNumber: dto.invoiceSerieNumber,
        installment: dto.installment,
        amountInstallment: dto.amountInstallment,
        netCommission: convertToBrCurrency(dto.netCommission),
        totalInvoiceAmount: convertToBrCurrency(dto.totalInvoiceAmount),
        totalNetCommission: convertToBrCurrency(dto.totalNetCommission),
        totalGrossCommission: convertToBrCurrency(dto.totalGrossCommission),
        finalCustomerName: dto.finalCustomerName,
        finalCustomerDocument: getFinalCustomerDocument(dto),
        salesOrderNumber: dto.salesOrderNumber.toString(),
        paymentCondition: dto.paymentCondition,
        billingDate: dateStringToModel(dto.billingDate),
        salesCommissionItems: this.toModelItems(dto.salesCommissionItems),
        salesCommissionInstallments: this.toModelInstallments(
          dto.salesCommissionInstallments,
        ),
      });
    } catch (error) {
      console.error('AdaptSalesCommissionError ->>', error);
      return new SalesCommission();
    }
  }

  private static toModelInstallments(inputDTO: SalesCommissionInstallmentInputDTO[]) {
    const dtoList = inputDTO.map((data) => new SalesCommissionInstallmentInputDTO(data));

    const totals: GeneralObject = {
      totalValue: 0,
      totalNetValue: 0,
    };

    const items = dtoList.map((dto) => {
      totals.totalValue += dto.value;
      totals.totalNetValue += dto.netValue;

      return new SalesCommissionInstallment({
        installment: dto.installment,
        status: adjustStatusToModel(dto.status),
        value: convertToBrCurrency(dto.value),
        netValue: convertToBrCurrency(dto.netValue),
        dueDate: dateStringToModel(dto.dueDate),
        paymentDate: dateStringToModel(dto.paymentDate),
      });
    });

    const totalsKeys = getObjectKeys<typeof totals>(totals);
    totalsKeys.forEach((key) => {
      totals[key] = convertToBrCurrency(totals[key]);
    });

    return {
      items,
      totals,
    };
  }

  private static toModelItems(inputDTO: SalesCommissionItemsInputDTO[]) {
    const dtoList = inputDTO.map((data) => new SalesCommissionItemsInputDTO(data));

    const totals: GeneralObject = {
      amountGrossValue: 0,
      amountIpiValue: 0,
      amountIcmsValue: 0,
      amountPisValue: 0,
      amountCofinsValue: 0,
      amountNetUnitPrice: 0,
    };

    const items = dtoList.map((data) => {
      totals.amountGrossValue += data.grossValue;
      totals.amountIpiValue += data.ipiValue;
      totals.amountIcmsValue += data.icmsValue;
      totals.amountPisValue += data.pisValue;
      totals.amountCofinsValue += data.cofinsValue;
      totals.amountNetUnitPrice += data.netUnitPrice;

      return new SalesCommissionItem({
        partNumber: data.partNumber,
        amount: data.amount.toString(),
        grossValue: convertToBrCurrency(data.grossValue),
        ipiValue: convertToBrCurrency(data.ipiValue),
        icmsValue: convertToBrCurrency(data.icmsValue),
        pisValue: convertToBrCurrency(data.pisValue),
        cofinsValue: convertToBrCurrency(data.cofinsValue),
        netUnitPrice: convertToBrCurrency(data.netUnitPrice),
      });
    });

    const totalsKeys = getObjectKeys<typeof totals>(totals);
    totalsKeys.forEach((key) => {
      totals[key] = convertToBrCurrency(totals[key]);
    });

    return {
      items,
      totals,
    };
  }
}

export class AdaptSalesCommissionListItem {
  public static toModel(
    inputDTO: SalesCommissionListItemInputDTO,
  ): SalesCommissionListItem {
    try {
      return new SalesCommissionListItem({
        salesCommissionId: inputDTO.salesCommissionId,
        finalCustomerDocument: getFinalCustomerDocument(inputDTO),
        finalCustomerName: inputDTO.finalCustomerName ?? '',
        invoiceNumber: inputDTO.invoiceNumber ?? '',
        invoiceSerieNumber: inputDTO.invoiceSerieNumber ?? '',
        salesOrderNumber: inputDTO.salesOrderNumber?.toString() ?? '',
        installment: inputDTO.installment ?? '',
        installmentAmount: inputDTO.installmentAmount ?? '',
        installmentDueDate: dateStringToModel(inputDTO.installmentDueDate),
        installmentPaymentDate: dateStringToModel(inputDTO.installmentPaymentDate),
        status: adjustStatusToModel(inputDTO.status),
        netValue: convertToBrCurrency(inputDTO.netValue),
      });
    } catch (error) {
      console.error('AdaptCommissionListItemError->>', error);

      const failedCommission = new SalesCommissionListItemInputDTO(inputDTO);
      failedCommission.status = 'Falha';

      return this.toModel(failedCommission);
    }
  }

  public static toModelList(inputDTOList: SalesCommissionListItemInputDTO[]) {
    return inputDTOList.map((inputDTO) => this.toModel(inputDTO));
  }
}

export class AdaptSalesCommissionFilters {
  public static toOutputDTO(
    filters: SalesCommissionFilters,
  ): SalesCommissionFiltersOutputDTO {
    const outputDTO = new SalesCommissionFiltersOutputDTO();

    const adaptDateRangeParams = (dateRange: Date[]): GeneralObject | undefined => {
      const [start, end] = dateRange;

      return start
        ? {
            start: formatToISODate(start),
            end: end ? formatToISODate(end) : formatToISODate(start),
          }
        : undefined;
    };

    const adaptPaymentDate = ({ paymentDate }: SalesCommissionFilters): void => {
      if (paymentDate && paymentDate.length > 0) {
        if (adaptDateRangeParams(paymentDate)) {
          outputDTO.paymentDateStart = adaptDateRangeParams(paymentDate)?.start;
          outputDTO.paymentDateEnd = adaptDateRangeParams(paymentDate)?.end;
        }
      }
    };

    const adaptDueDate = ({ dueDate }: SalesCommissionFilters): void => {
      if (dueDate && dueDate.length > 0) {
        if (adaptDateRangeParams(dueDate)) {
          outputDTO.dueDateStart = adaptDateRangeParams(dueDate)?.start;
          outputDTO.dueDateEnd = adaptDateRangeParams(dueDate)?.end;
        }
      }
    };

    const adaptFilterOption = ({ filterOption, filterValue }: SalesCommissionFilters) => {
      if (!filterOption) return;
      if (!filterValue || filterValue === '') return;

      const filterOptionsMapper = new Map([
        ['INVOICE_NUMBER', 'invoiceNumber'],
        ['INVOICE_SERIE', 'invoiceSerieNumber'],
        ['ORDER_NUMBER', 'salesOrderNumber'],
        ['COMPANY_DOCUMENT', 'finalCustomerDocument'],
        ['FINAL_CUSTOMER_NAME', 'finalCustomerName'],
      ]);

      const filterKey = filterOptionsMapper.get(filterOption);

      if (!filterKey) {
        console.error('AdaptSalesCommissionFiltersError ->> Invalid filter option');
        return;
      }

      outputDTO.filteredField = filterKey;
      outputDTO.genericTerm = filterValue;
    };

    const adaptStatus = ({ commissionStatus }: SalesCommissionFilters): void => {
      if (!commissionStatus) return;

      const statusOutputDTO =
        SalesCommissionStatusEnum[
          commissionStatus as keyof typeof SalesCommissionStatusEnum
        ];

      outputDTO.commissionStatus = statusOutputDTO;
    };

    adaptStatus(filters);
    adaptPaymentDate(filters);
    adaptDueDate(filters);
    adaptFilterOption(filters);
    return outputDTO;
  }
}

function getFinalCustomerDocument({
  finalCustomerDocument,
}: SalesCommissionListItemInputDTO | SalesCommissionInputDTO):
  | CompanyDocument
  | PersonalDocument
  | string {
  if (!finalCustomerDocument) return '';
  if (new CompanyDocument(finalCustomerDocument).isCompanyDocumentLike)
    return new CompanyDocument(finalCustomerDocument);
  if (new PersonalDocument(finalCustomerDocument).isValid)
    return new PersonalDocument(finalCustomerDocument);
  console.error('AdaptCommissionListItemError->> Invalid document');
  return 'INVALID_DOCUMENT';
}

function adjustStatusToModel(status: string): {
  color: string;
  content: string;
} {
  const statusInputDTO =
    SalesCommissionStatusDTOEnum[status as keyof typeof SalesCommissionStatusDTOEnum];
  const statusModel = SalesCommissionStatusEnum[statusInputDTO];
  return COMMISSION_STATUS[statusModel as keyof typeof COMMISSION_STATUS];
}

function dateStringToModel(date: string): string {
  if (!!date && date !== '0001-01-01T00:00:00') {
    return formatToBrazilianDate(new Date(date));
  }
  return '-';
}
